import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { Button } from "../../ui/components/Button";
import { useConfirm } from "../../ui/components/ConfirmDialog";

type Device = {
  deviceId: string;
  status: "unregistered" | "registered" | "for_sale";
  name: string | null;
  timezone: string;
  schedulePrefs: { hourMin: number; hourMax: number } | null;
  lastSeen: string | null;
  firmwareVersion: string | null;
};

type DeviceListResponse = {
  devices: Device[];
};

type EditState = {
  name: string;
  timezone: string;
  hourMin: string;
  hourMax: string;
};

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
`;

const Container = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #232323;
  box-shadow: 6px 6px #232323;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Inputs = styled.div`
  margin-top: 0.8rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.6rem;
`;

const Input = styled.input`
  border: 1px solid #232323;
  border-radius: 4px;
  padding: 0.5rem 0.65rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #414141;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.8rem;
`;

const Muted = styled.p`
  color: #5f5f5f;
`;

const ErrorText = styled.p`
  color: #a30000;
  font-weight: 600;
`;

const formatLastSeen = (value: string | null): string => {
  if (!value) return "never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleString();
};

const toEditState = (device: Device): EditState => ({
  name: device.name || "",
  timezone: device.timezone || "UTC",
  hourMin: String(device.schedulePrefs?.hourMin ?? 0),
  hourMax: String(device.schedulePrefs?.hourMax ?? 23),
});

const AotdIrlDevices: React.FC = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const auth = useAppSelector((state) => state.auth);
  const isLoggedIn = Boolean(auth.token);

  const [devices, setDevices] = useState<Device[]>([]);
  const [editing, setEditing] = useState<Record<string, EditState>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyDevice, setBusyDevice] = useState<string | null>(null);

  const empty = useMemo(() => !loading && devices.length === 0, [devices.length, loading]);

  const loadDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<DeviceListResponse>("/api/aotd-irl/devices");
      const nextDevices = res.data.devices || [];
      setDevices(nextDevices);
      setEditing((prev) => {
        const next: Record<string, EditState> = {};
        nextDevices.forEach((device) => {
          next[device.deviceId] = prev[device.deviceId] || toEditState(device);
        });
        return next;
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    loadDevices();
  }, [isLoggedIn]);

  const goToLogin = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    navigate(`/login?return=${returnUrl}`);
  };

  const updateField = (deviceId: string, key: keyof EditState, value: string) => {
    setEditing((prev) => ({
      ...prev,
      [deviceId]: {
        ...(prev[deviceId] || {
          name: "",
          timezone: "UTC",
          hourMin: "0",
          hourMax: "23",
        }),
        [key]: value,
      },
    }));
  };

  const saveConfig = async (deviceId: string) => {
    const draft = editing[deviceId];
    if (!draft) return;

    const hourMin = Math.max(0, Math.min(23, Number.parseInt(draft.hourMin || "0", 10) || 0));
    const hourMax = Math.max(0, Math.min(23, Number.parseInt(draft.hourMax || "23", 10) || 23));

    const payload: Record<string, any> = {
      name: draft.name,
      timezone: draft.timezone || "UTC",
      schedulePrefs: { hourMin, hourMax },
    };

    setBusyDevice(deviceId);
    setError(null);
    try {
      await apiClient.patch(`/api/aotd-irl/devices/${encodeURIComponent(deviceId)}/config`, payload);
      await loadDevices();
      // refresh edit state after successful save
    } catch (err: any) {
      setError(err?.response?.data?.error || "failed to update config");
    } finally {
      setBusyDevice(null);
    }
  };

  const unclaim = async (deviceId: string) => {
    const confirmed = await confirm({
      title: "Unclaim this device?",
      message: `Device id: ${deviceId}. You'll lose access and need to re-pair to get it back.`,
      confirmLabel: "Unclaim",
      tone: "danger",
    });
    if (!confirmed) return;

    setBusyDevice(deviceId);
    setError(null);
    try {
      await apiClient.delete(`/api/aotd-irl/devices/${encodeURIComponent(deviceId)}`);
      await loadDevices();
    } catch (err: any) {
      setError(err?.response?.data?.error || "failed to unclaim device");
    } finally {
      setBusyDevice(null);
    }
  };

  if (!isLoggedIn) {
    return (
      <Wrap>
        <Container>
          <Card>
            <h1 style={{ marginTop: 0 }}>AOTD IRL devices</h1>
            <Muted>sign in to view and manage your boards.</Muted>
            <Button variant="sky" onClick={goToLogin}>sign in</Button>
          </Card>
        </Container>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Container>
        <Card>
          <h1 style={{ marginTop: 0, marginBottom: "0.35rem" }}>AOTD IRL devices</h1>
          <Muted style={{ marginTop: 0 }}>
            update board settings and push new config revisions to your claimed devices.
          </Muted>
          <Button variant="secondary" onClick={loadDevices} loading={loading}>refresh list</Button>
          {error && <ErrorText>{error}</ErrorText>}
        </Card>

        {empty && (
          <Card>
            <Muted style={{ margin: 0 }}>no claimed devices yet.</Muted>
          </Card>
        )}

        {devices.map((device) => {
          const draft = editing[device.deviceId] || toEditState(device);
          const isBusy = busyDevice === device.deviceId;

          return (
            <Card key={device.deviceId}>
              <h3 style={{ marginTop: 0, marginBottom: "0.2rem" }}>
                {draft.name.trim() || device.deviceId}
              </h3>
              <Muted style={{ margin: 0 }}>
                id: {device.deviceId} · status: {device.status} · last seen: {formatLastSeen(device.lastSeen)}
              </Muted>
              {device.firmwareVersion && (
                <Muted style={{ marginTop: "0.25rem" }}>
                  firmware: {device.firmwareVersion}
                </Muted>
              )}

              <Inputs>
                <Label>
                  name
                  <Input value={draft.name} onChange={(e) => updateField(device.deviceId, "name", e.target.value)} />
                </Label>
                <Label>
                  timezone
                  <Input value={draft.timezone} onChange={(e) => updateField(device.deviceId, "timezone", e.target.value)} />
                </Label>
                <Label>
                  wake hour min
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={draft.hourMin}
                    onChange={(e) => updateField(device.deviceId, "hourMin", e.target.value)}
                  />
                </Label>
                <Label>
                  wake hour max
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={draft.hourMax}
                    onChange={(e) => updateField(device.deviceId, "hourMax", e.target.value)}
                  />
                </Label>
              </Inputs>

              <Row>
                <Button
                  variant="default"
                  onClick={() => saveConfig(device.deviceId)}
                  loading={isBusy}
                  disabled={isBusy}
                >
                  save config
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => unclaim(device.deviceId)}
                  disabled={isBusy}
                >
                  unclaim device
                </Button>
              </Row>
            </Card>
          );
        })}
      </Container>
    </Wrap>
  );
};

export default AotdIrlDevices;
